import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * The mount-agnostic base path for a mini-app (e.g. `/tools/command-center`
 * or `/flow/command-center`). Apps consume this via `useAppBasePath()`
 * instead of hardcoding their own mount, so the same app component can be
 * dual-mounted under `/tools` (MiniAppLayout) and `/flow` (FlowShell).
 *
 * Explicit context over deriving from `useLocation()`: the host mount
 * already knows its own base path, so having it hand that value down is
 * simpler and more testable than regex-parsing `location.pathname` inside
 * every app — and it's the only option for apps (like Command Center) that
 * render chrome above their own `<Routes>`, where there is no scoped
 * relative-path routing to lean on.
 */
export const MiniAppBasePathContext = createContext<string | null>(null);

interface MiniAppBasePathProviderProps {
  basePath: string;
  children: ReactNode;
}

/**
 * Provides the mini-app's base path to everything it renders. Must be
 * mounted by the HOST (MiniAppLayout / FlowShell) wrapping the app's whole
 * lazy component — not by a route inside the app — because some apps (e.g.
 * Command Center's `GlobalSearch`) render above their own `<Routes>` and
 * would otherwise never see the value.
 */
export function MiniAppBasePathProvider({ basePath, children }: MiniAppBasePathProviderProps) {
  return (
    <MiniAppBasePathContext.Provider value={basePath}>
      {children}
    </MiniAppBasePathContext.Provider>
  );
}

/**
 * Reads the current mini-app's base path. Throws a descriptive error when
 * called outside a `MiniAppBasePathProvider` — a missing provider is a host
 * wiring bug and must fail loud rather than silently returning an empty
 * base path that would produce broken (relative-looking) links.
 */
export function useAppBasePath(): string {
  const basePath = useContext(MiniAppBasePathContext);
  if (basePath == null) {
    throw new Error(
      'useAppBasePath() was called outside a <MiniAppBasePathProvider>. ' +
        'The host mount (MiniAppLayout or FlowShell) must wrap this app\'s ' +
        'lazy component in a provider before it renders.',
    );
  }
  return basePath;
}
